/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { get as getUser } from 'js/lib/user';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import type { InlineNotificationProps } from '@coursera/cds-core';

import _t from 'i18n!nls/account-settings';

type Props = {
  handleCloseModal: () => void;
  handleDataRequest: () => void;
  isEnterpriseLearner: boolean;
  disabled: boolean;
  showModal: boolean;
  notificationType?: InlineNotificationProps['severity'];
  notificationMessage?: string;
  onDismissNotification: () => void;
};

const styles = {
  notificationContainer: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  sendButton: css`
    margin-right: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-100);
  `,
  cancelButton: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

const DataReportModal: React.FC<Props> = ({
  handleCloseModal,
  handleDataRequest,
  isEnterpriseLearner,
  disabled,
  showModal,
  notificationMessage,
  notificationType,
  onDismissNotification,
}) => (
  <Dialog className="rc-DataReport" onClose={handleCloseModal} open={showModal}>
    <Dialog.HeadingGroup>{_t('Confirm Recipient of Data Report')}</Dialog.HeadingGroup>
    <Dialog.Content>
      <Typography2 component="p" variant="bodyPrimary">
        {_t('This data report will be sent to #{email}.', { email: getUser().email_address })}
      </Typography2>
      <br />
      <Typography2 component="h3" variant="subtitleLarge">
        {_t('Are you sure?')}
      </Typography2>
    </Dialog.Content>
    <Dialog.Actions>
      <div aria-live="polite" css={notificationMessage && notificationType && styles.notificationContainer}>
        {notificationMessage && notificationType && (
          <InlineNotification onDismiss={onDismissNotification} severity={notificationType}>
            <Typography2 component="span">{notificationMessage}</Typography2>
          </InlineNotification>
        )}
      </div>
      <Button
        data-track={true}
        variant="primary"
        onClick={handleDataRequest}
        disabled={disabled}
        css={styles.sendButton}
      >
        {!disabled || (disabled && !!notificationMessage && !!notificationType) ? _t('Yes, send') : _t('Sending')}
      </Button>
      <Button variant="secondary" onClick={handleCloseModal} css={styles.cancelButton}>
        {_t('I need to update my email')}
      </Button>
      {isEnterpriseLearner && (
        <div className="caption-text m-t-1s">
          <Typography2 component="span" variant="bodySecondary">
            <FormattedHTMLMessage
              message={_t(
                `If you are a learner with enterprise enrollments, please contact us via the 
                <a href="https://learner.coursera.help/" rel="noopener noreferrer" target="_blank">
                learner help center
              </a> to get your full data release.
              `
              )}
            />
          </Typography2>
        </div>
      )}
    </Dialog.Actions>
  </Dialog>
);

export default DataReportModal;
